import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { PageTransitionProvider } from './context/PageTransitionContext';
import PageTransition from './components/PageTransition';
import { detectBot, shouldTrackAnalytics, track } from './lib/analytics';

const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const GalleryExterieurs = lazy(() => import('./pages/GalleryExterieurs'));
const GalleryInterieurs = lazy(() => import('./pages/GalleryInterieurs'));
const GalleryAerienne = lazy(() => import('./pages/GalleryAerienne'));
const Web3D = lazy(() => import('./pages/Web3D'));
const InstallationSceno = lazy(() => import('./pages/InstallationSceno'));
const Metrics = lazy(() => import('./pages/Metrics'));

function RouteFallback() {
  return <div className="route-fallback" aria-hidden="true" />;
}

function isMetricsPath(pathname) {
  return pathname.replace(/\/+$/, '').split('/').pop().toLowerCase() === 'metrics';
}

// ── PageTracker ───────────────────────────────────────────────────────────────
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!shouldTrackAnalytics() || isMetricsPath(location.pathname)) return;

    sessionStorage.setItem('last_page', location.pathname);

    const key = `page_tracked_${location.pathname}`;
    if (sessionStorage.getItem(key)) return;

    track({ action: 'page', page: location.pathname });

    sessionStorage.setItem(key, 'true');
  }, [location.pathname]);

  return null;
}

// ── SessionTracker ────────────────────────────────────────────────────────────
function SessionTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!shouldTrackAnalytics()) return;

    if (!sessionStorage.getItem('session_tracked')) {
      const bot = detectBot();

      if (bot) {
        // Bot détecté — on envoie nom, catégorie, page d'entrée
        track({
          action: 'botVisit',
          name: bot.name,
          category: bot.category,
          page: location.pathname,
          ua: bot.ua.slice(0, 200), // tronqué pour éviter URL trop longue
        });
      } else {
        // Humain — tracking normal
        const isMobile    = navigator.maxTouchPoints > 1 || window.innerWidth <= 768;
        const device      = isMobile ? 'mobile' : 'desktop';
        const isReturning = !!localStorage.getItem('quarx_visited');
        const visitorType = isReturning ? 'returning' : 'new';
        if (!isReturning) localStorage.setItem('quarx_visited', 'true');
        track({ action: 'session', device, visitor: visitorType });
      }
      sessionStorage.setItem('session_tracked', 'true');
    }

    const sendExit = () => {
      if (sessionStorage.getItem('exit_sent')) return;
      const lastPage = sessionStorage.getItem('last_page') || '/';
      if (lastPage === '/' || isMetricsPath(lastPage)) return;
      sessionStorage.setItem('exit_sent', 'true');
      track({ action: 'exit', page: lastPage }, { keepalive: true });
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') sendExit();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', sendExit);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', sendExit);
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Trackers — uniquement sur les pages hors /metrics ─────────────────────────
function Trackers() {
  const location = useLocation();
  if (isMetricsPath(location.pathname)) return null;
  return (
    <>
      <PageTracker />
      <SessionTracker />
    </>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
function getRouterBasename() {
  const publicUrl = process.env.PUBLIC_URL;
  if (!publicUrl || publicUrl === '.') return '/';

  const publicPathname = new URL(publicUrl, window.location.origin).pathname;
  const normalized = publicPathname.endsWith('/') ? publicPathname.slice(0, -1) : publicPathname;
  if (!normalized) return '/';

  const { pathname } = window.location;

  return pathname === normalized || pathname.startsWith(`${normalized}/`) ? normalized : '/';
}

function App() {
  const basename = getRouterBasename();

  return (
    <Router basename={basename} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PageTransitionProvider>
        <Trackers />
        <Routes>
          <Route
            path="/metrics"
            element={
              <Suspense fallback={<RouteFallback />}>
                <Metrics />
              </Suspense>
            }
          />
          <Route path="*" element={
            <PageTransition>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/Info" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/exterieurs" element={<GalleryExterieurs />} />
                  <Route path="/interieurs" element={<GalleryInterieurs />} />
                  <Route path="/aerienne" element={<GalleryAerienne />} />
                  <Route path="/web3d" element={<Web3D />} />
                  <Route path="/installations" element={<InstallationSceno />} />
                </Routes>
              </Suspense>
            </PageTransition>
          } />
        </Routes>
      </PageTransitionProvider>
    </Router>
  );
}

export default App;
